// hexToRgb/rgbToLab are system-agnostic and reused directly from the NCS picker rather than duplicated here

import { Model_RalColorGet } from "src/backend/openapi/v1";
import { hexToRgb, rgbToLab } from "../ncs-color-picker/ncs-color-utils";

const RECENT_COLORS_STORAGE_KEY = "ralColorPicker.recentColors";
const RECENT_COLORS_MAX = 8;

// strips whitespace and the "RAL" prefix so stored codes ("RAL 1000") and user input ("1000", "ral 1000")
// normalize to the same comparable string
const normalizeRalSearchTerm = (value: string): string => value.trim().toUpperCase().replace(/\s+/g, "").replace(/^RAL/, "");

export const ralCodeMatchesSearch = (code: string, name: string, searchTerm: string): boolean => {
  const trimmedTerm = searchTerm.trim();
  if (!trimmedTerm) return true;

  const normalizedTerm = normalizeRalSearchTerm(trimmedTerm);
  if (normalizedTerm && normalizeRalSearchTerm(code).includes(normalizedTerm)) return true;

  return name.toLowerCase().includes(trimmedTerm.toLowerCase());
};

const labDistanceSquared = (a: [number, number, number], b: [number, number, number]): number => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;

// this runs on every color-picker drag-move event, so hot-path callers should precompute each
// catalogue entry's Lab value once (e.g. via useMemo) and pass it as `precomputedLabs`, in the same
// order as `colors`, rather than re-deriving all of them on every call
export const findNearestRalColor = (colors: Model_RalColorGet[], rgb: [number, number, number], precomputedLabs?: Array<[number, number, number]>): Model_RalColorGet | undefined => {
  if (colors.length === 0) return undefined;

  const targetLab = rgbToLab(rgb);
  const labs = precomputedLabs ?? colors.map(color => rgbToLab(hexToRgb(color.hex)));

  let nearestColor = colors[0];
  let nearestDistance = labDistanceSquared(targetLab, labs[0]);

  for (let i = 1; i < colors.length; i++) {
    const distance = labDistanceSquared(targetLab, labs[i]);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestColor = colors[i];
    }
  }

  return nearestColor;
};

export const getRecentRalColorCodes = (): string[] => {
  try {
    const raw = window.localStorage.getItem(RECENT_COLORS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((code): code is string => typeof code === "string") : [];
  } catch {
    // localStorage can throw (private browsing, quota, corrupted JSON) - treat as "no recents"
    return [];
  }
};

export const addRecentRalColorCode = (code: string): string[] => {
  const existing = getRecentRalColorCodes().filter(existingCode => existingCode !== code);
  const updated = [code, ...existing].slice(0, RECENT_COLORS_MAX);

  try {
    window.localStorage.setItem(RECENT_COLORS_STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // best-effort cache - ignore write failures
  }

  return updated;
};
